import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddPlus, DeleteTrashRemove } from '../../../assets/icons';
import {
  useCreatePriceGuardrailMutation,
  useUpdatePriceGuardrailMutation,
} from '../../../services/priceGuardrails.api';
import { useFetchModelsQuery } from '../../../services/provision';
import { ChargerModel } from '../../../stores/types/chargers.interface';
import { PackageType } from '../../../stores/types/feePackage.interface';
import {
  GuardrailCondition,
  PriceGuardrail,
} from '../../../stores/types/priceGuardrails.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Dropdown,
  FormInput,
  Icon,
  Label,
  LabelType,
  Snackbar,
  useGlobalModalContext,
} from '../../_ui';
import { Form } from '../../_ui/Form.component';
import { AlertPosition, AlertType } from '../../_ui/snack-bar/Snack-Bar.component';

interface Props {
  guardrail?: PriceGuardrail;
}

interface DropdownItem {
  id: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
}

const GUARDRAIL_TYPES = ['CHARGER_MODEL', 'STATE_OR_REGION', 'PLAN'] as const;

const PLAN_OPTIONS = [
  PackageType.ESSENTIALS_PLUS,
  PackageType.BASIC_PLAN,
  PackageType.FLEX_PLAN,
  PackageType.PRO_PLAN,
  PackageType.ELA,
  PackageType.ESSENTIALS,
];

const ALL_RESTRICTED_RULES = [
  'BILLED_BY_TIME_PLUGGED_IN',
  'BILLED_BY_EFFECTIVE_CHARGING_TIME',
  'BILLED_BY_KWH',
  'FLAT_RATE',
  'IDLE_RATE',
  'TIERED',
] as const;

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
];

export const EditGuardrailModal = memo(({ guardrail }: Props) => {
  const isEdit = !!guardrail;
  const { t } = useTranslation();
  const { hideModal } = useGlobalModalContext();
  const [createGuardrail, createResult] = useCreatePriceGuardrailMutation();
  const [updateGuardrail, updateResult] = useUpdatePriceGuardrailMutation();
  const mutationResult = isEdit ? updateResult : createResult;

  const handleQueryFailed = () => {
    const err = mutationResult.error as { data?: { message?: string }; status?: number };
    Snackbar.show({
      message:
        err?.data?.message || t('support_section.guardrail_save_error', 'Failed to save guardrail'),
      alertType: AlertType.ERROR,
      position: AlertPosition.BOTTOM_LEFT,
      duration: 5000,
    });
    hideModal();
  };

  const { data: modelsData = [] } = useFetchModelsQuery(undefined, {
    selectFromResult: (endpoint) => ({
      data: (endpoint.data ?? []) as ChargerModel[],
    }),
  });

  const chargerModelOptions = useMemo(() => {
    const models = new Set<string>();
    modelsData.forEach((m: ChargerModel) => {
      if (m.model) models.add(m.model);
    });
    return Array.from(models).sort();
  }, [modelsData]);

  const [conditions, setConditions] = useState<GuardrailCondition[]>(
    guardrail ? [...guardrail.conditions] : [{ guardrailType: '', matchValue: '' }],
  );
  const [restrictedRules, setRestrictedRules] = useState<string[]>(
    guardrail ? [...guardrail.restrictedPricingRules] : [],
  );
  const [messages, setMessages] = useState<Record<string, string>>(
    guardrail ? { ...guardrail.message } : {},
  );

  const handleConditionChange = (index: number, field: keyof GuardrailCondition, value: string) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'guardrailType') {
      updated[index].matchValue = '';
    }
    setConditions(updated);
  };

  const addCondition = () => {
    setConditions([...conditions, { guardrailType: '', matchValue: '' }]);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleMessageChange = (langCode: string, value: string) => {
    setMessages((prev) => ({ ...prev, [langCode]: value }));
  };

  const handleSave = () => {
    const incompleteCondition = conditions.some((c) => !c.guardrailType || !c.matchValue);
    if (incompleteCondition) {
      Snackbar.show({
        message: 'All conditions must have a type and value.',
        alertType: AlertType.ERROR,
        position: AlertPosition.BOTTOM_LEFT,
        duration: 5000,
      });
      return;
    }

    if (restrictedRules.length === 0) {
      Snackbar.show({
        message: 'At least one restricted rule is required.',
        alertType: AlertType.ERROR,
        position: AlertPosition.BOTTOM_LEFT,
        duration: 5000,
      });
      return;
    }

    const cleanMessages = Object.fromEntries(
      Object.entries(messages).filter(([, v]) => v.trim() !== ''),
    );

    if (!cleanMessages.en) {
      Snackbar.show({
        message: t('support_section.guardrail_english_message_required'),
        alertType: AlertType.ERROR,
        position: AlertPosition.BOTTOM_LEFT,
        duration: 5000,
      });
      return;
    }

    const payload = {
      conditions,
      restrictedPricingRules: restrictedRules,
      message: cleanMessages,
    };

    if (isEdit) {
      updateGuardrail({ id: guardrail.id, ...payload });
    } else {
      createGuardrail(payload);
    }
  };

  const usedGuardrailTypes = conditions.map((c) => c.guardrailType).filter(Boolean);

  const buildGuardrailTypeItems = (selectedType: string) => {
    const otherUsed = usedGuardrailTypes.filter((usedType) => usedType !== selectedType);
    return GUARDRAIL_TYPES.map((type) => ({
      id: type,
      label: type.replaceAll('_', ' '),
      selected: type === selectedType,
      selectedIds: otherUsed,
    }));
  };

  const buildMatchValueItems = (guardrailType: string, currentValue: string) => {
    if (guardrailType === 'CHARGER_MODEL') {
      return chargerModelOptions.map((model) => ({
        id: model,
        label: model,
        selected: model === currentValue,
      }));
    }
    if (guardrailType === 'PLAN') {
      return PLAN_OPTIONS.map((plan) => ({
        id: plan,
        label: plan.replaceAll('_', ' '),
        selected: plan === currentValue,
      }));
    }
    return null;
  };

  const renderMatchValueInput = (condition: GuardrailCondition, index: number) => {
    const dropdownItems = buildMatchValueItems(condition.guardrailType, condition.matchValue);

    if (dropdownItems) {
      return (
        <div className='flex-1'>
          <Label
            text={t('support_section.guardrail_match_value')}
            type={LabelType.LABEL_XS}
            color={ColorType.GREY6}
            className='mb-1'
          />
          <Dropdown
            headerWidth='100%'
            items={dropdownItems}
            onItemClick={(item: DropdownItem) =>
              handleConditionChange(index, 'matchValue', item.id)
            }
            labelType={LabelType.BODY3_G4}
            labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerHighLightClassName='bg-grey1 border-grey3 rounded h-[36px] pr-2'
            headerClassName='bg-grey1 border-grey3 rounded pr-2'
            headerHeight='36px'
          />
        </div>
      );
    }

    return (
      <div className='flex-1'>
        <Label
          text={
            condition.guardrailType === 'STATE_OR_REGION'
              ? 'State / Region'
              : t('support_section.guardrail_match_value')
          }
          type={LabelType.LABEL_XS}
          color={ColorType.GREY6}
          className='mb-1'
        />
        <FormInput
          defaultValue={condition.matchValue}
          placeholder={
            condition.guardrailType === 'STATE_OR_REGION' ? '2-letter code, e.g. ON, MI, CA' : ''
          }
          onChange={(e) => {
            const val = e.target.value.toUpperCase().slice(0, 2);
            handleConditionChange(index, 'matchValue', val);
          }}
          width='100%'
        />
      </div>
    );
  };

  return (
    <Form
      onSubmit={handleSave}
      queryResult={mutationResult}
      onQuerySuccess={hideModal}
      onQueryFailed={handleQueryFailed}
    >
      <div className='flex flex-col gap-5'>
        <div className='flex flex-col gap-3'>
          <div className='flex items-center justify-between'>
            <Label
              text={t('support_section.guardrail_conditions')}
              type={LabelType.H5}
              color={ColorType.BLACK}
            />
            <Button
              label={t('support_section.guardrail_add_condition')}
              type={ButtonType.TERTIARY}
              size={ButtonSize.SMALL}
              icon={AddPlus}
              onClick={addCondition}
              disabled={conditions.length >= GUARDRAIL_TYPES.length}
            />
          </div>
          {conditions.map((condition, index) => (
            <div key={index} className='flex items-end gap-2'>
              <div className='flex-1'>
                <Label
                  text={t('support_section.guardrail_type')}
                  type={LabelType.LABEL_XS}
                  color={ColorType.GREY6}
                  className='mb-1'
                />
                <Dropdown
                  headerWidth='100%'
                  items={buildGuardrailTypeItems(condition.guardrailType)}
                  onItemClick={(item: DropdownItem) =>
                    handleConditionChange(index, 'guardrailType', item.id)
                  }
                  labelType={LabelType.BODY3_G4}
                  labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                  chevdownIconHighlightColor={ColorType.GREY6}
                  headerHighLightClassName='bg-grey1 border-grey3 rounded h-[36px] pr-2'
                  headerClassName='bg-grey1 border-grey3 rounded pr-2'
                  headerHeight='36px'
                />
              </div>
              {renderMatchValueInput(condition, index)}
              {conditions.length > 1 && (
                <Icon
                  src={DeleteTrashRemove}
                  color={ColorType.NEGATIVE}
                  className='cursor-pointer mb-2'
                  onClick={() => removeCondition(index)}
                  isFocusable
                />
              )}
            </div>
          ))}
        </div>

        <div className='flex flex-col gap-3'>
          <div className='flex items-center justify-between'>
            <Label
              text={t('support_section.price_guardrails_restricted_rules')}
              type={LabelType.H5}
              color={ColorType.BLACK}
            />
            <Button
              label={t('support_section.guardrail_add_rule')}
              type={ButtonType.TERTIARY}
              size={ButtonSize.SMALL}
              icon={AddPlus}
              onClick={() => {
                const next = ALL_RESTRICTED_RULES.find((r) => !restrictedRules.includes(r));
                if (next) setRestrictedRules([...restrictedRules, next]);
              }}
              disabled={restrictedRules.length >= ALL_RESTRICTED_RULES.length}
            />
          </div>
          {restrictedRules.map((rule, index) => (
            <div key={index} className='flex items-end gap-2'>
              <div className='flex-1'>
                <Dropdown
                  headerWidth='100%'
                  items={ALL_RESTRICTED_RULES.map((r) => ({
                    id: r,
                    label: r.replaceAll('_', ' '),
                    selected: r === rule,
                    selectedIds: restrictedRules.filter((rr) => rr !== rule),
                  }))}
                  onItemClick={(item: DropdownItem) => {
                    const updated = [...restrictedRules];
                    updated[index] = item.id;
                    setRestrictedRules(updated);
                  }}
                  labelType={LabelType.BODY3_G4}
                  labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                  chevdownIconHighlightColor={ColorType.GREY6}
                  headerHighLightClassName='bg-grey1 border-grey3 rounded h-[36px] pr-2'
                  headerClassName='bg-grey1 border-grey3 rounded pr-2'
                  headerHeight='36px'
                />
              </div>
              {restrictedRules.length > 1 && (
                <Icon
                  src={DeleteTrashRemove}
                  color={ColorType.NEGATIVE}
                  className='cursor-pointer mb-2'
                  onClick={() => setRestrictedRules(restrictedRules.filter((_, i) => i !== index))}
                  isFocusable
                />
              )}
            </div>
          ))}
        </div>

        <div className='flex flex-col gap-3'>
          <Label
            text={t('support_section.price_guardrails_message')}
            type={LabelType.H5}
            color={ColorType.BLACK}
          />
          {LANGUAGES.map((lang) => (
            <FormInput
              key={lang.code}
              label={lang.label}
              defaultValue={messages[lang.code] ?? ''}
              placeholder={`${t('support_section.price_guardrails_message')} (${lang.label})`}
              onChange={(e) => handleMessageChange(lang.code, e.target.value)}
              width='100%'
            />
          ))}
        </div>

        <div className='flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-2'>
          <Button
            label={t('cancel')}
            type={ButtonType.TERTIARY}
            size={ButtonSize.SMALL}
            onClick={hideModal}
          />
          <Button label={t('save')} type={ButtonType.PRIMARY} size={ButtonSize.SMALL} isSumbit />
        </div>
      </div>
    </Form>
  );
});

EditGuardrailModal.displayName = 'EditGuardrailModal';
